import { useEffect } from 'react';

import { ESCAPE_KEY, TEST_ID } from './constants';
import type { FunctionComponent, MouseEvent, Props } from './types';

import styles from './styles.module.scss';

const ConfirmModal: FunctionComponent<Props> = ({
    cancelLabel,
    className = '',
    confirmLabel,
    isConfirmDisabled = false,
    isDestructive = false,
    isOpen,
    message,
    onCancel,
    onConfirm,
    testId = TEST_ID,
    title
}: Props) => {
    useEffect(() => {
        if (!isOpen) {
            return undefined;
        }

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === ESCAPE_KEY) {
                onCancel();
            }
        };

        document.addEventListener('keydown', handleKeyDown);

        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onCancel]);

    if (!isOpen) {
        return null;
    }

    const handleOverlayClick = () => {
        onCancel();
    };

    const handleCardClick = (event: MouseEvent<HTMLDivElement>) => {
        event.stopPropagation();
    };

    const confirmClassName = `${styles.confirmButton}${
        isDestructive ? ` ${styles.destructive}` : ''
    }`;

    return (
        <div
            aria-modal
            className={`${styles.confirmModal} ${className}`}
            data-testid={testId}
            onClick={handleOverlayClick}
            role="dialog"
        >
            <div className={styles.card} onClick={handleCardClick}>
                <h2 className={styles.title}>{title}</h2>

                <p className={styles.message}>{message}</p>

                <div className={styles.actions}>
                    <button className={styles.cancelButton} onClick={onCancel} type="button">
                        {cancelLabel}
                    </button>

                    <button
                        className={confirmClassName}
                        disabled={isConfirmDisabled}
                        onClick={onConfirm}
                        type="button"
                    >
                        {confirmLabel}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ConfirmModal;
